import { createSelector } from 'reselect';
import { RootState } from '../../store/store';


// Constants for cart statuses and refund statuses
export const CART_STATUS = {
  ALL: 999,
  PRE_COMMITED: 0,
  OPENED: 1,
  IN_PROGRESS: 2,
  DELIVERY: 3,
  READY: 4,
  FINISHED: 5,
  CANCELLATION_REQUEST: 6,
  CANCELLED: 7,
} as const;

export const REFUND_STATUS = {
  NEW: 0,
  IN_PROGRESS: 1,
  APPROVED: 2,
  REJECTED: 3,
} as const;

export type CartStatus = (typeof CART_STATUS)[keyof typeof CART_STATUS];
export type RefundStatus = (typeof REFUND_STATUS)[keyof typeof REFUND_STATUS];

// Interface for text and color scheme properties of statuses
interface StatusProps {
  text: string;
  colorScheme: string;
}

export const selectSuppliersResults = (state: RootState) => state.carts.serchDetailsBySuppliers;
export const selectIsLoadingSuppliers = (state: RootState) => state.carts.isLoading.suppliers;


// Mapping for cart statuses with text and color schemes
export const cartStatusMap: Record<CartStatus, StatusProps> = {
  [CART_STATUS.ALL]: { text: 'Все', colorScheme: 'transparent' },
  [CART_STATUS.PRE_COMMITED]: { text: 'Не подтверждена', colorScheme: 'gray' },
  [CART_STATUS.OPENED]: { text: 'Открыта', colorScheme: 'green' },
  [CART_STATUS.IN_PROGRESS]: { text: 'В работе', colorScheme: 'orange' },
  [CART_STATUS.DELIVERY]: { text: 'Доставка', colorScheme: 'blue' },
  [CART_STATUS.READY]: { text: 'Готова к выдаче', colorScheme: 'cyan' },
  [CART_STATUS.FINISHED]: { text: 'Завершена', colorScheme: 'green' },
  [CART_STATUS.CANCELLATION_REQUEST]: { text: 'Запрос на отмену', colorScheme: 'yellow' },
  [CART_STATUS.CANCELLED]: { text: 'Отменена', colorScheme: 'red' },
};

// Mapping for refund statuses with text and color schemes
export const refundStatusMap: Record<RefundStatus, StatusProps> = {
  [REFUND_STATUS.NEW]: { text: 'Новый', colorScheme: 'blue' },
  [REFUND_STATUS.IN_PROGRESS]: { text: 'В обработке', colorScheme: 'orange' },
  [REFUND_STATUS.APPROVED]: { text: 'Подтвержден', colorScheme: 'green' },
  [REFUND_STATUS.REJECTED]: { text: 'Отклонен', colorScheme: 'red' },
};

// Default status for unknown cases
export const defaultStatus: StatusProps = {
  text: 'Неизвестный статус',
  colorScheme: 'gray',
};

// Base selectors for carts
const cartsListSelector = (state: RootState) => state.carts.list;
const currentCartSelector = (state: RootState) => state.carts.currentCart;
const isCartsLoadingSelector = (state: RootState) => state.carts.isLoading.list;
const isCartLoadingSelector = (state: RootState) => state.carts.isLoading.single;
const cartsErrorSelector = (state: RootState) => state.carts.errors.list;
const cartErrorSelector = (state: RootState) => state.carts.errors.single;

// Selectors for carts
export const selectAllCarts = createSelector(cartsListSelector, list => list || null);
export const selectCurrentCart = createSelector(currentCartSelector, currentCart => currentCart);
export const selectIsLoadingCarts = createSelector(isCartsLoadingSelector, isLoading => isLoading);
export const selectIsLoadingCart = createSelector(isCartLoadingSelector, isLoading => isLoading);
export const selectCartsError = createSelector(cartsErrorSelector, error => error || '');
export const selectCartError = createSelector(cartErrorSelector, error => error || '');

const suppliersStateSelector = (state: RootState) => state.carts.suppliers;

export const selectSuppliersList = createSelector(
  suppliersStateSelector,
  suppliers => suppliers || [],
);

// // Selector for filtering carts by status
// export const selectCartsByStatus = (status: number) =>
//   createSelector(cartsListSelector, list =>
//     (list || []).filter((cart: CartDTO) => cart.status === status),
//   );

// // Selector for filtering carts by status, date range, and search term
// export const selectFilteredCarts = (
//   status: CartStatus | 'all',
//   startDate: Date | null,
//   endDate: Date | null,
//   searchTerm: string,
// ) =>
//   createSelector(cartsListSelector, carts => {
//     return (carts || []).filter((cart: CartDTO) => {
//       const matchesStatus = status === CART_STATUS.ALL || cart.status === status;

//       const cartDate = new Date(cart.created_at);
//       const matchesStartDate = startDate ? cartDate >= startDate : true;
//       const matchesEndDate = endDate ? cartDate <= endDate : true;

//       const matchesSearchTerm = cart.order_items.some(item =>
//         item.good_name.toLowerCase().includes(searchTerm.toLowerCase()),
//       );

//       return matchesStatus && matchesStartDate && matchesEndDate && matchesSearchTerm;
//     });
//   });

// // Selector for filtering carts based on tab selection, date range, and search term
// export const selectFilteredCartsByTab = (
//   tab: CartStatus,
//   startDate: Date | null,
//   endDate: Date | null,
//   searchTerm: string,
// ) =>
//   createSelector(cartsListSelector, carts => {
//     return (carts || []).filter((cart: CartDTO) => {
//       let matchesTab = true;

//       // Tab-based filtering logic
//       switch (tab) {
//         case CART_STATUS.IN_PROGRESS:
//           matchesTab = cart.status === CART_STATUS.IN_PROGRESS;
//           break;
//         case CART_STATUS.CANCELLATION_REQUEST:
//           matchesTab = cart.status === CART_STATUS.CANCELLATION_REQUEST;
//           break;
//         case CART_STATUS.CANCELLED:
//           matchesTab = cart.status === CART_STATUS.CANCELLED;
//           break;
//         case CART_STATUS.DELIVERY:
//           matchesTab = cart.status === CART_STATUS.DELIVERY;
//           break;
//         case CART_STATUS.READY:
//           matchesTab = cart.status === CART_STATUS.READY;
//           break;
//         default:
//           matchesTab = true; // Show all for "Все"
//       }

//       // Filter by date range
//       const cartDate = new Date(cart.created_at);
//       const matchesStartDate = startDate ? cartDate >= startDate : true;
//       const matchesEndDate = endDate ? cartDate <= endDate : true;

//       // Filter by search term
//       const matchesSearchTerm = cart.order_items.some(item =>
//         item.good_name.toLowerCase().includes(searchTerm.toLowerCase()),
//       );

//       return matchesTab && matchesStartDate && matchesEndDate && matchesSearchTerm;
//     });
//   });
